/**
 * Sheet Metal Workbench for CAD Viewer
 * Implements complete sheet metal design operations including:
 * - Base flange creation
 * - Wall/Flange from edges
 * - Hem operations
 * - Junctions
 * - Bend operations
 * - Unfold/Fold (flatten pattern)
 * - Corner relief
 * - K-factor calculations
 */

class SheetMetalWorkbench {
  constructor(cadViewer) {
    this.cadViewer = cadViewer;
    this.scene = cadViewer.scene;
    this.camera = cadViewer.camera;

    // Sheet metal specific state
    this.baseFlanges = new Map(); // Map<id, BaseFlange>
    this.features = new Map();     // Map<id, Feature>
    this.activePartId = null;
    this.selectedEdges = [];
    this.selectedFaces = [];

    // Default sheet metal parameters
    this.defaultParams = {
      thickness: 1.0,        // mm
      bendRadius: 1.5,       // mm
      kFactor: 0.44,         // Bend deduction factor (0.3-0.5 typical)
      reliefType: 'rectangle', // 'rectangle', 'round', 'tear'
      reliefRatio: 1.0,      // Relief depth as ratio of thickness
      bendAngle: 90,         // degrees
      flangeLength: 10,      // mm
      autoMiter: true        // Auto-calculate miters at corners
    };

    // Visual helpers
    this.edgeHighlights = [];
    this.faceHighlights = [];
    this.bendLineVisuals = [];

    // Unfold state
    this.unfoldedParts = new Map(); // Map<partId, UnfoldedState>

    this.initialize();
  }

  /**
   * Initialize the workbench
   */
  initialize() {
    console.log('[SheetMetalWorkbench] Initializing...');
    this.setupEventListeners();
    this.setupSelectionHandlers();
  }

  /**
   * Setup event listeners for sheet metal UI
   */
  setupEventListeners() {
    // Will be wired up from cad-viewer.js
  }

  /**
   * Setup selection handlers for edges and faces
   */
  setupSelectionHandlers() {
    // Mouse interaction for edge/face selection
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
  }

  // ============================================================================
  // BASE FLANGE OPERATIONS
  // ============================================================================

  /**
   * Create a base flange (starting sheet metal part)
   * @param {Object} params - {width, length, thickness, sketch}
   * @returns {Object} Base flange feature
   */
  createBaseFlange(params) {
    const {
      width = 100,
      length = 100,
      thickness = this.defaultParams.thickness,
      sketch = null,
      position = new THREE.Vector3(0, 0, 0)
    } = params;

    console.log('[SheetMetal] Creating base flange:', { width, length, thickness });

    const flangeId = `base_flange_${Date.now()}`;

    let geometry;

    if (sketch) {
      // Create flange from sketch profile
      geometry = this.createFlangeFromSketch(sketch, thickness);
    } else {
      // Create rectangular base flange
      geometry = new THREE.BoxGeometry(width, length, thickness);
    }

    const material = new THREE.MeshPhongMaterial({
      color: 0x90A4AE,
      metalness: 0.8,
      roughness: 0.2,
      side: THREE.DoubleSide
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(position);
    mesh.userData.sheetMetalType = 'base_flange';
    mesh.userData.id = flangeId;
    mesh.userData.thickness = thickness;
    mesh.userData.bendRadius = this.defaultParams.bendRadius;
    mesh.userData.kFactor = this.defaultParams.kFactor;
    mesh.userData.unfoldable = true;

    this.scene.add(mesh);

    const feature = {
      id: flangeId,
      type: 'base_flange',
      name: 'Base Flange',
      mesh: mesh,
      params: { width, length, thickness, position },
      edges: this.extractEdges(mesh),
      faces: this.extractFaces(mesh),
      bendLines: []
    };

    this.baseFlanges.set(flangeId, feature);
    this.features.set(flangeId, feature);
    this.activePartId = flangeId;

    // Add to CAD feature tree
    if (this.cadViewer.document) {
      this.cadViewer.document.addFeature({
        type: 'SheetMetal_BaseFlange',
        name: 'Base Flange',
        params: feature.params,
        visible: true
      });
    }

    console.log('[SheetMetal] Base flange created:', flangeId);
    return feature;
  }

  /**
   * Create flange from sketch profile
   */
  createFlangeFromSketch(sketch, thickness) {
    // Extract sketch path and extrude
    const shape = this.convertSketchToShape(sketch);
    const extrudeSettings = {
      depth: thickness,
      bevelEnabled: false
    };

    return new THREE.ExtrudeGeometry(shape, extrudeSettings);
  }

  /**
   * Convert sketch to THREE.Shape
   */
  convertSketchToShape(sketch) {
    const shape = new THREE.Shape();

    if (!sketch.entities || sketch.entities.length === 0) {
      // Default rectangle
      shape.moveTo(-50, -50);
      shape.lineTo(50, -50);
      shape.lineTo(50, 50);
      shape.lineTo(-50, 50);
      shape.lineTo(-50, -50);
      return shape;
    }

    // Build shape from sketch entities
    const entities = sketch.entities;
    const firstPoint = entities[0].start || entities[0].center;
    shape.moveTo(firstPoint.x, firstPoint.y);

    entities.forEach(entity => {
      if (entity.type === 'line') {
        shape.lineTo(entity.end.x, entity.end.y);
      } else if (entity.type === 'arc') {
        const center = entity.center;
        const radius = entity.radius;
        const startAngle = Math.atan2(entity.start.y - center.y, entity.start.x - center.x);
        const endAngle = Math.atan2(entity.end.y - center.y, entity.end.x - center.x);
        shape.absarc(center.x, center.y, radius, startAngle, endAngle, false);
      }
    });

    return shape;
  }

  // ============================================================================
  // WALL & FLANGE OPERATIONS
  // ============================================================================

  /**
   * Create a wall/flange from selected edge
   * @param {Object} params - {edge, angle, length, bendRadius}
   * @returns {Object} Wall feature
   */
  createWallFromEdge(params) {
    const {
      edge = this.selectedEdges[0],
      angle = this.defaultParams.bendAngle,
      length = this.defaultParams.flangeLength,
      bendRadius = this.defaultParams.bendRadius,
      thickness = this.defaultParams.thickness,
      kFactor = this.defaultParams.kFactor
    } = params;

    if (!edge) {
      console.warn('[SheetMetal] No edge selected for wall creation');
      return null;
    }

    console.log('[SheetMetal] Creating wall from edge:', { angle, length });

    const wallId = `wall_${Date.now()}`;

    // Calculate wall geometry
    const wallGeometry = this.calculateWallGeometry(edge, angle, length, thickness, bendRadius);

    const material = new THREE.MeshPhongMaterial({
      color: 0x90A4AE,
      metalness: 0.8,
      roughness: 0.2,
      side: THREE.DoubleSide
    });

    const mesh = new THREE.Mesh(wallGeometry, material);
    mesh.userData.sheetMetalType = 'wall';
    mesh.userData.id = wallId;
    mesh.userData.thickness = thickness;
    mesh.userData.bendRadius = bendRadius;
    mesh.userData.bendAngle = angle;
    mesh.userData.kFactor = kFactor;

    this.scene.add(mesh);

    // Calculate bend deduction
    const bendDeduction = this.calculateBendDeduction(thickness, bendRadius, angle, kFactor);

    const feature = {
      id: wallId,
      type: 'wall',
      name: `Wall (${angle}°)`,
      mesh: mesh,
      params: { edge, angle, length, thickness, bendRadius, bendDeduction },
      bendLine: edge,
      parentId: this.activePartId
    };

    this.features.set(wallId, feature);

    // Add bend line visualization
    this.addBendLineVisualization(edge, bendRadius);

    // Add to CAD feature tree
    if (this.cadViewer.document) {
      this.cadViewer.document.addFeature({
        type: 'SheetMetal_Wall',
        name: feature.name,
        params: feature.params,
        visible: true
      });
    }

    console.log('[SheetMetal] Wall created:', wallId);
    return feature;
  }

  /**
   * Calculate wall geometry from edge
   */
  calculateWallGeometry(edge, angle, length, thickness, bendRadius) {
    // Get edge direction and position
    const edgeStart = edge.start || new THREE.Vector3();
    const edgeEnd = edge.end || new THREE.Vector3();
    const edgeDir = new THREE.Vector3().subVectors(edgeEnd, edgeStart).normalize();
    const edgeLength = edgeStart.distanceTo(edgeEnd);

    // Calculate normal (perpendicular to edge, in plane)
    const normal = new THREE.Vector3(-edgeDir.y, edgeDir.x, 0).normalize();

    // Calculate bend angle in radians
    const angleRad = (angle * Math.PI) / 180;

    // Create wall profile with bend
    const shape = new THREE.Shape();

    // Start at bend line
    shape.moveTo(0, 0);

    // Add bend radius arc
    const arcLength = bendRadius * angleRad;
    const segments = Math.max(8, Math.floor(angle / 15));

    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const a = angleRad * t;
      const x = bendRadius * Math.sin(a);
      const y = bendRadius * (1 - Math.cos(a));
      shape.lineTo(x, y);
    }

    // Extend to full length
    const finalX = bendRadius * Math.sin(angleRad) + (length - arcLength) * Math.cos(angleRad);
    const finalY = bendRadius * (1 - Math.cos(angleRad)) + (length - arcLength) * Math.sin(angleRad);
    shape.lineTo(finalX, finalY);

    // Extrude along edge
    const extrudePath = new THREE.LineCurve3(edgeStart, edgeEnd);
    const extrudeSettings = {
      extrudePath: extrudePath,
      steps: 1,
      bevelEnabled: false
    };

    // For simple case, create box geometry
    const geometry = new THREE.BoxGeometry(edgeLength, length, thickness);

    // Rotate to align with bend angle
    const rotationMatrix = new THREE.Matrix4();
    rotationMatrix.makeRotationX(angleRad);
    geometry.applyMatrix4(rotationMatrix);

    // Position at edge
    const position = new THREE.Vector3()
      .addVectors(edgeStart, edgeEnd)
      .multiplyScalar(0.5);
    const translationMatrix = new THREE.Matrix4();
    translationMatrix.makeTranslation(position.x, position.y + length / 2, position.z);
    geometry.applyMatrix4(translationMatrix);

    return geometry;
  }

  /**
   * Create flange on edge (similar to wall but with more options)
   */
  createFlange(params) {
    const {
      edge = this.selectedEdges[0],
      angle = 90,
      length = 10,
      bendRadius = this.defaultParams.bendRadius,
      flangeType = 'perpendicular', // 'perpendicular', 'parallel', 'angled'
      offset = 0,
      relief = true
    } = params;

    console.log('[SheetMetal] Creating flange:', { angle, length, flangeType });

    // Create wall with additional flange features
    const wall = this.createWallFromEdge({
      edge,
      angle,
      length,
      bendRadius,
      thickness: this.defaultParams.thickness,
      kFactor: this.defaultParams.kFactor
    });

    if (relief) {
      // Add corner relief at flange ends
      this.addCornerRelief(wall, this.defaultParams.reliefType);
    }

    return wall;
  }

  // ============================================================================
  // HEM OPERATIONS
  // ============================================================================

  /**
   * Create hem (fold edge back on itself)
   * @param {Object} params - {edge, hemType, length, gap}
   * @returns {Object} Hem feature
   */
  createHem(params) {
    const {
      edge = this.selectedEdges[0],
      hemType = 'open', // 'open', 'closed', 'teardrop', 'rolled'
      length = 5,
      gap = 0.5,
      radius = 1
    } = params;

    if (!edge) {
      console.warn('[SheetMetal] No edge selected for hem');
      return null;
    }

    console.log('[SheetMetal] Creating hem:', { hemType, length });

    const hemId = `hem_${Date.now()}`;
    const thickness = this.defaultParams.thickness;

    // Calculate hem geometry based on type
    let geometry;
    switch (hemType) {
      case 'open':
        geometry = this.createOpenHem(edge, length, gap, thickness);
        break;
      case 'closed':
        geometry = this.createClosedHem(edge, length, thickness);
        break;
      case 'teardrop':
        geometry = this.createTeardropHem(edge, radius, thickness);
        break;
      case 'rolled':
        geometry = this.createRolledHem(edge, radius, thickness);
        break;
      default:
        geometry = this.createOpenHem(edge, length, gap, thickness);
    }

    const material = new THREE.MeshPhongMaterial({
      color: 0x90A4AE,
      metalness: 0.8,
      roughness: 0.2,
      side: THREE.DoubleSide
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData.sheetMetalType = 'hem';
    mesh.userData.id = hemId;
    mesh.userData.hemType = hemType;

    this.scene.add(mesh);

    const feature = {
      id: hemId,
      type: 'hem',
      name: `Hem (${hemType})`,
      mesh: mesh,
      params: { edge, hemType, length, gap, radius },
      parentId: this.activePartId
    };

    this.features.set(hemId, feature);

    // Add to CAD feature tree
    if (this.cadViewer.document) {
      this.cadViewer.document.addFeature({
        type: 'SheetMetal_Hem',
        name: feature.name,
        params: feature.params,
        visible: true
      });
    }

    return feature;
  }

  /**
   * Create open hem geometry
   */
  createOpenHem(edge, length, gap, thickness) {
    const edgeStart = edge.start || new THREE.Vector3();
    const edgeEnd = edge.end || new THREE.Vector3();
    const edgeLength = edgeStart.distanceTo(edgeEnd);

    // Create bent geometry
    const curve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, length * 0.3, 0),
      new THREE.Vector3(0, length * 0.7, -gap),
      new THREE.Vector3(0, length, -gap)
    ]);

    const tubeGeometry = new THREE.TubeGeometry(curve, 20, thickness / 2, 8, false);

    return tubeGeometry;
  }

  /**
   * Create closed hem geometry
   */
  createClosedHem(edge, length, thickness) {
    const edgeStart = edge.start || new THREE.Vector3();
    const edgeEnd = edge.end || new THREE.Vector3();
    const edgeLength = edgeStart.distanceTo(edgeEnd);

    // Create 180-degree fold
    const radius = length / Math.PI;
    const curve = new THREE.ArcCurve(0, 0, radius, 0, Math.PI, false);

    const points = curve.getPoints(20);
    const path3d = points.map(p => new THREE.Vector3(p.x, p.y, 0));

    const curve3d = new THREE.CatmullRomCurve3(path3d);
    const tubeGeometry = new THREE.TubeGeometry(curve3d, 20, thickness / 2, 8, false);

    return tubeGeometry;
  }

  /**
   * Create teardrop hem geometry
   */
  createTeardropHem(edge, radius, thickness) {
    // Create teardrop shape
    const shape = new THREE.Shape();
    shape.absarc(0, 0, radius, 0, Math.PI, false);
    shape.lineTo(-radius, -thickness);
    shape.lineTo(radius, -thickness);

    const extrudeSettings = {
      depth: 1,
      bevelEnabled: false
    };

    return new THREE.ExtrudeGeometry(shape, extrudeSettings);
  }

  /**
   * Create rolled hem geometry
   */
  createRolledHem(edge, radius, thickness) {
    // Create 360-degree roll
    const curve = new THREE.ArcCurve(0, 0, radius, 0, Math.PI * 2, false);
    const points = curve.getPoints(30);
    const path3d = points.map(p => new THREE.Vector3(p.x, p.y, 0));

    const curve3d = new THREE.CatmullRomCurve3(path3d);
    const tubeGeometry = new THREE.TubeGeometry(curve3d, 30, thickness / 2, 8, true);

    return tubeGeometry;
  }

  // ============================================================================
  // JUNCTION OPERATIONS
  // ============================================================================

  /**
   * Create junction to connect two walls
   * @param {Object} params - {wall1, wall2, junctionType}
   * @returns {Object} Junction feature
   */
  createJunction(params) {
    const {
      wall1,
      wall2,
      junctionType = 'butt', // 'butt', 'miter', 'overlap'
      gap = 0
    } = params;

    if (!wall1 || !wall2) {
      console.warn('[SheetMetal] Two walls required for junction');
      return null;
    }

    console.log('[SheetMetal] Creating junction:', junctionType);

    const junctionId = `junction_${Date.now()}`;

    // Calculate junction geometry
    const geometry = this.calculateJunctionGeometry(wall1, wall2, junctionType, gap);

    const material = new THREE.MeshPhongMaterial({
      color: 0xFFB74D,
      metalness: 0.7,
      roughness: 0.3,
      side: THREE.DoubleSide
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData.sheetMetalType = 'junction';
    mesh.userData.id = junctionId;

    this.scene.add(mesh);

    const feature = {
      id: junctionId,
      type: 'junction',
      name: `Junction (${junctionType})`,
      mesh: mesh,
      params: { wall1: wall1.id, wall2: wall2.id, junctionType, gap },
      parentId: this.activePartId
    };

    this.features.set(junctionId, feature);

    return feature;
  }

  /**
   * Calculate junction geometry
   */
  calculateJunctionGeometry(wall1, wall2, junctionType, gap) {
    // Simple butt junction - create connecting piece
    const geometry = new THREE.BoxGeometry(2, 2, this.defaultParams.thickness);

    // Position at intersection
    // TODO: Calculate actual intersection point

    return geometry;
  }

  // ============================================================================
  // BEND OPERATIONS
  // ============================================================================

  /**
   * Create a bend along a line
   * @param {Object} params - {line, angle, radius, kFactor}
   * @returns {Object} Bend feature
   */
  createBend(params) {
    const {
      line = this.selectedEdges[0],
      angle = 90,
      radius = this.defaultParams.bendRadius,
      kFactor = this.defaultParams.kFactor,
      direction = 'up' // 'up' or 'down'
    } = params;

    if (!line) {
      console.warn('[SheetMetal] No line selected for bend');
      return null;
    }

    console.log('[SheetMetal] Creating bend:', { angle, radius });

    const bendId = `bend_${Date.now()}`;
    const thickness = this.defaultParams.thickness;

    // Calculate bend allowance
    const bendAllowance = this.calculateBendAllowance(thickness, radius, angle, kFactor);
    const bendDeduction = this.calculateBendDeduction(thickness, radius, angle, kFactor);

    // Visualize bend line
    this.addBendLineVisualization(line, radius);

    const feature = {
      id: bendId,
      type: 'bend',
      name: `Bend (${angle}°)`,
      bendLine: line,
      params: {
        angle,
        radius,
        kFactor,
        bendAllowance,
        bendDeduction,
        direction
      },
      parentId: this.activePartId
    };

    this.features.set(bendId, feature);

    // Add to CAD feature tree
    if (this.cadViewer.document) {
      this.cadViewer.document.addFeature({
        type: 'SheetMetal_Bend',
        name: feature.name,
        params: feature.params,
        visible: true
      });
    }

    return feature;
  }

  /**
   * Add bend line visualization
   */
  addBendLineVisualization(line, radius) {
    const start = line.start || new THREE.Vector3();
    const end = line.end || new THREE.Vector3();

    // Create dashed line
    const points = [start, end];
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineDashedMaterial({
      color: 0xFF6B6B,
      linewidth: 2,
      dashSize: 3,
      gapSize: 1
    });

    const bendLine = new THREE.Line(geometry, material);
    bendLine.computeLineDistances();
    bendLine.userData.isBendLine = true;

    this.scene.add(bendLine);
    this.bendLineVisuals.push(bendLine);

    // Add bend radius indicator
    const radiusCircle = new THREE.CircleGeometry(radius, 32);
    const radiusMaterial = new THREE.LineBasicMaterial({ color: 0xFF6B6B, transparent: true, opacity: 0.3 });
    const radiusLine = new THREE.LineLoop(radiusCircle, radiusMaterial);

    const midPoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
    radiusLine.position.copy(midPoint);
    radiusLine.userData.isBendLine = true;

    this.scene.add(radiusLine);
    this.bendLineVisuals.push(radiusLine);
  }

  // ============================================================================
  // UNFOLD/FOLD OPERATIONS
  // ============================================================================

  /**
   * Unfold sheet metal part to flat pattern
   * @param {string} partId - Part to unfold
   * @returns {Object} Unfolded state
   */
  unfoldPart(partId = this.activePartId) {
    if (!partId) {
      console.warn('[SheetMetal] No active part to unfold');
      return null;
    }

    console.log('[SheetMetal] Unfolding part:', partId);

    // Check if already unfolded
    if (this.unfoldedParts.has(partId)) {
      console.warn('[SheetMetal] Part already unfolded');
      return this.unfoldedParts.get(partId);
    }

    const baseFlangeFeature = this.baseFlanges.get(partId);
    if (!baseFlangeFeature) {
      console.warn('[SheetMetal] Base flange not found');
      return null;
    }

    // Collect all features that are part of this part
    const partFeatures = Array.from(this.features.values())
      .filter(f => f.id === partId || f.parentId === partId);

    // Store original state
    const originalState = {
      features: partFeatures.map(f => ({
        id: f.id,
        position: f.mesh ? f.mesh.position.clone() : null,
        rotation: f.mesh ? f.mesh.rotation.clone() : null,
        geometry: f.mesh ? f.mesh.geometry.clone() : null
      }))
    };

    // Calculate flat pattern
    const flatPattern = this.calculateFlatPattern(partFeatures);

    // Create flat pattern mesh
    const flatMesh = this.createFlatPatternMesh(flatPattern);
    flatMesh.position.y = 50; // Offset to show unfolded view
    this.scene.add(flatMesh);

    // Hide original features
    partFeatures.forEach(f => {
      if (f.mesh) {
        f.mesh.visible = false;
      }
    });

    // Hide bend line visuals
    this.bendLineVisuals.forEach(v => {
      v.visible = false;
    });

    const unfoldedState = {
      partId,
      originalState,
      flatPattern,
      flatMesh,
      timestamp: Date.now()
    };

    this.unfoldedParts.set(partId, unfoldedState);

    console.log('[SheetMetal] Part unfolded successfully');
    return unfoldedState;
  }

  /**
   * Fold back the unfolded part
   * @param {string} partId - Part to fold back
   */
  foldPart(partId = this.activePartId) {
    if (!partId) {
      console.warn('[SheetMetal] No active part to fold');
      return;
    }

    console.log('[SheetMetal] Folding part:', partId);

    const unfoldedState = this.unfoldedParts.get(partId);
    if (!unfoldedState) {
      console.warn('[SheetMetal] Part is not unfolded');
      return;
    }

    // Remove flat pattern mesh
    this.scene.remove(unfoldedState.flatMesh);
    if (unfoldedState.flatMesh.geometry) {
      unfoldedState.flatMesh.geometry.dispose();
    }

    // Restore original features
    unfoldedState.originalState.features.forEach(savedFeature => {
      const feature = this.features.get(savedFeature.id);
      if (feature && feature.mesh) {
        feature.mesh.visible = true;
        if (savedFeature.position) {
          feature.mesh.position.copy(savedFeature.position);
        }
        if (savedFeature.rotation) {
          feature.mesh.rotation.copy(savedFeature.rotation);
        }
      }
    });

    // Show bend line visuals
    this.bendLineVisuals.forEach(v => {
      v.visible = true;
    });

    this.unfoldedParts.delete(partId);

    console.log('[SheetMetal] Part folded successfully');
  }

  /**
   * Calculate flat pattern from features
   */
  calculateFlatPattern(features) {
    const thickness = this.defaultParams.thickness;
    const segments = [];

    // Start with base flange
    const baseFlange = features.find(f => f.type === 'base_flange');
    if (baseFlange) {
      const params = baseFlange.params;
      segments.push({
        type: 'rectangle',
        width: params.width,
        length: params.length,
        position: new THREE.Vector2(0, 0),
        rotation: 0
      });
    }

    // Add walls as flat segments
    const walls = features.filter(f => f.type === 'wall');
    let offsetY = baseFlange ? baseFlange.params.length / 2 : 0;

    walls.forEach(wall => {
      const params = wall.params;
      const bendAllowance = this.calculateBendAllowance(
        thickness,
        params.bendRadius,
        params.angle,
        params.kFactor
      );

      offsetY += bendAllowance;

      segments.push({
        type: 'rectangle',
        width: baseFlange ? baseFlange.params.width : 100,
        length: params.length,
        position: new THREE.Vector2(0, offsetY),
        rotation: 0,
        bendLine: {
          y: offsetY - bendAllowance / 2,
          radius: params.bendRadius
        }
      });

      offsetY += params.length / 2;
    });

    return {
      segments,
      totalLength: offsetY * 2,
      thickness,
      boundingBox: this.calculateFlatPatternBounds(segments)
    };
  }

  /**
   * Calculate bounding box for flat pattern
   */
  calculateFlatPatternBounds(segments) {
    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;

    segments.forEach(seg => {
      const halfW = seg.width / 2;
      const halfL = seg.length / 2;
      const x = seg.position.x;
      const y = seg.position.y;

      minX = Math.min(minX, x - halfW);
      maxX = Math.max(maxX, x + halfW);
      minY = Math.min(minY, y - halfL);
      maxY = Math.max(maxY, y + halfL);
    });

    return {
      min: new THREE.Vector2(minX, minY),
      max: new THREE.Vector2(maxX, maxY),
      width: maxX - minX,
      height: maxY - minY
    };
  }

  /**
   * Create mesh for flat pattern visualization
   */
  createFlatPatternMesh(flatPattern) {
    const group = new THREE.Group();
    const thickness = flatPattern.thickness;

    // Create each segment
    flatPattern.segments.forEach((seg, index) => {
      const geometry = new THREE.BoxGeometry(seg.width, seg.length, thickness);
      const material = new THREE.MeshPhongMaterial({
        color: 0xB0BEC5,
        metalness: 0.6,
        roughness: 0.4,
        side: THREE.DoubleSide
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(seg.position.x, seg.position.y, 0);
      mesh.rotation.z = seg.rotation;

      group.add(mesh);

      // Add bend line markers
      if (seg.bendLine) {
        const bendLineGeometry = new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(-seg.width / 2, seg.bendLine.y, thickness / 2),
          new THREE.Vector3(seg.width / 2, seg.bendLine.y, thickness / 2)
        ]);
        const bendLineMaterial = new THREE.LineDashedMaterial({
          color: 0xFF6B6B,
          linewidth: 3,
          dashSize: 5,
          gapSize: 2
        });
        const bendLine = new THREE.Line(bendLineGeometry, bendLineMaterial);
        bendLine.computeLineDistances();
        group.add(bendLine);
      }
    });

    // Add dimensions
    this.addFlatPatternDimensions(group, flatPattern);

    group.userData.isFlatPattern = true;

    return group;
  }

  /**
   * Add dimension annotations to flat pattern
   */
  addFlatPatternDimensions(group, flatPattern) {
    // Add dimension lines and text
    const bounds = flatPattern.boundingBox;

    // Overall width dimension
    const widthDimY = bounds.max.y + 10;
    const widthLine = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(bounds.min.x, widthDimY, 0),
      new THREE.Vector3(bounds.max.x, widthDimY, 0)
    ]);
    const dimMaterial = new THREE.LineBasicMaterial({ color: 0x4CAF50 });
    group.add(new THREE.Line(widthLine, dimMaterial));

    // Overall height dimension
    const heightDimX = bounds.max.x + 10;
    const heightLine = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(heightDimX, bounds.min.y, 0),
      new THREE.Vector3(heightDimX, bounds.max.y, 0)
    ]);
    group.add(new THREE.Line(heightLine, dimMaterial));
  }

  // ============================================================================
  // CORNER RELIEF
  // ============================================================================

  /**
   * Add corner relief to prevent material tearing at bends
   * @param {Object} feature - Feature to add relief to
   * @param {string} reliefType - 'rectangle', 'round', 'tear'
   */
  addCornerRelief(feature, reliefType = this.defaultParams.reliefType) {
    console.log('[SheetMetal] Adding corner relief:', reliefType);

    const thickness = this.defaultParams.thickness;
    const reliefSize = thickness * this.defaultParams.reliefRatio;

    // Create relief geometry
    let reliefGeometry;
    switch (reliefType) {
      case 'rectangle':
        reliefGeometry = this.createRectangleRelief(reliefSize, thickness);
        break;
      case 'round':
        reliefGeometry = this.createRoundRelief(reliefSize, thickness);
        break;
      case 'tear':
        reliefGeometry = this.createTearRelief(reliefSize, thickness);
        break;
      default:
        reliefGeometry = this.createRectangleRelief(reliefSize, thickness);
    }

    // Add relief to feature
    // This would typically use CSG operations to subtract relief from the feature
    console.log('[SheetMetal] Corner relief added');
  }

  /**
   * Create rectangular relief geometry
   */
  createRectangleRelief(size, thickness) {
    return new THREE.BoxGeometry(size, size, thickness * 1.1);
  }

  /**
   * Create round relief geometry
   */
  createRoundRelief(size, thickness) {
    return new THREE.CylinderGeometry(size / 2, size / 2, thickness * 1.1, 16);
  }

  /**
   * Create tear relief geometry
   */
  createTearRelief(size, thickness) {
    // V-shaped relief
    const shape = new THREE.Shape();
    shape.moveTo(0, 0);
    shape.lineTo(size, size / 2);
    shape.lineTo(0, size);
    shape.lineTo(0, 0);

    return new THREE.ExtrudeGeometry(shape, {
      depth: thickness * 1.1,
      bevelEnabled: false
    });
  }

  // ============================================================================
  // K-FACTOR CALCULATIONS
  // ============================================================================

  /**
   * Calculate bend allowance using K-factor
   * BA = (π/180) × Angle × (Radius + K × Thickness)
   */
  calculateBendAllowance(thickness, radius, angle, kFactor) {
    const angleRad = (angle * Math.PI) / 180;
    const bendAllowance = angleRad * (radius + kFactor * thickness);
    return bendAllowance;
  }

  /**
   * Calculate bend deduction
   * BD = 2 × (Radius + Thickness) × tan(Angle/2) - BA
   */
  calculateBendDeduction(thickness, radius, angle, kFactor) {
    const angleRad = (angle * Math.PI) / 180;
    const bendAllowance = this.calculateBendAllowance(thickness, radius, angle, kFactor);
    const outsideSetback = (radius + thickness) * Math.tan(angleRad / 2);
    const bendDeduction = 2 * outsideSetback - bendAllowance;
    return bendDeduction;
  }

  /**
   * Calculate flat length for a bent section
   */
  calculateFlatLength(leg1, leg2, angle, thickness, radius, kFactor) {
    const bendDeduction = this.calculateBendDeduction(thickness, radius, angle, kFactor);
    const flatLength = leg1 + leg2 - bendDeduction;
    return flatLength;
  }

  /**
   * Calculate K-factor from bend deduction
   * Used when K-factor is not known but BD is measured
   */
  calculateKFactor(thickness, radius, angle, bendDeduction) {
    const angleRad = (angle * Math.PI) / 180;
    const outsideSetback = (radius + thickness) * Math.tan(angleRad / 2);
    const bendAllowance = 2 * outsideSetback - bendDeduction;
    const kFactor = (bendAllowance / angleRad - radius) / thickness;
    return kFactor;
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Extract edges from mesh
   */
  extractEdges(mesh) {
    const edges = [];
    const geometry = mesh.geometry;

    if (!geometry) return edges;

    const edgesGeometry = new THREE.EdgesGeometry(geometry);
    const positions = edgesGeometry.attributes.position;

    for (let i = 0; i < positions.count; i += 2) {
      const start = new THREE.Vector3(
        positions.getX(i),
        positions.getY(i),
        positions.getZ(i)
      );
      const end = new THREE.Vector3(
        positions.getX(i + 1),
        positions.getY(i + 1),
        positions.getZ(i + 1)
      );

      edges.push({ start, end, index: i / 2 });
    }

    return edges;
  }

  /**
   * Extract faces from mesh
   */
  extractFaces(mesh) {
    const faces = [];
    const geometry = mesh.geometry;

    if (!geometry || !geometry.index) return faces;

    const positions = geometry.attributes.position;
    const indices = geometry.index;

    for (let i = 0; i < indices.count; i += 3) {
      const a = indices.getX(i);
      const b = indices.getX(i + 1);
      const c = indices.getX(i + 2);

      const vA = new THREE.Vector3(positions.getX(a), positions.getY(a), positions.getZ(a));
      const vB = new THREE.Vector3(positions.getX(b), positions.getY(b), positions.getZ(b));
      const vC = new THREE.Vector3(positions.getX(c), positions.getY(c), positions.getZ(c));

      // Calculate normal
      const normal = new THREE.Vector3();
      const edge1 = new THREE.Vector3().subVectors(vB, vA);
      const edge2 = new THREE.Vector3().subVectors(vC, vA);
      normal.crossVectors(edge1, edge2).normalize();

      faces.push({
        vertices: [vA, vB, vC],
        normal,
        index: i / 3
      });
    }

    return faces;
  }

  /**
   * Select edge for operations
   */
  selectEdge(edge) {
    this.selectedEdges = [edge];
    this.highlightEdge(edge);
  }

  /**
   * Highlight selected edge
   */
  highlightEdge(edge) {
    // Remove previous highlights
    this.clearEdgeHighlights();

    // Create highlight
    const points = [edge.start, edge.end];
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({ color: 0x00FF00, linewidth: 3 });
    const highlight = new THREE.Line(geometry, material);

    this.scene.add(highlight);
    this.edgeHighlights.push(highlight);
  }

  /**
   * Clear edge highlights
   */
  clearEdgeHighlights() {
    this.edgeHighlights.forEach(h => {
      this.scene.remove(h);
      if (h.geometry) h.geometry.dispose();
      if (h.material) h.material.dispose();
    });
    this.edgeHighlights = [];
  }

  /**
   * Export flat pattern as DXF (conceptual)
   */
  exportFlatPattern(partId = this.activePartId) {
    const unfoldedState = this.unfoldedParts.get(partId);
    if (!unfoldedState) {
      console.warn('[SheetMetal] Part must be unfolded first');
      return null;
    }

    const flatPattern = unfoldedState.flatPattern;

    // Generate DXF data
    const dxfData = {
      type: 'SheetMetalFlatPattern',
      segments: flatPattern.segments,
      dimensions: flatPattern.boundingBox,
      thickness: flatPattern.thickness,
      material: 'Steel',
      bendLines: flatPattern.segments
        .filter(s => s.bendLine)
        .map(s => s.bendLine)
    };

    console.log('[SheetMetal] Flat pattern export data:', dxfData);
    return dxfData;
  }

  /**
   * Get sheet metal parameters for UI
   */
  getParameters() {
    return { ...this.defaultParams };
  }

  /**
   * Update sheet metal parameters
   */
  updateParameters(params) {
    Object.assign(this.defaultParams, params);
    console.log('[SheetMetal] Parameters updated:', this.defaultParams);
  }

  /**
   * Clean up resources
   */
  dispose() {
    // Clear highlights
    this.clearEdgeHighlights();

    // Remove bend line visuals
    this.bendLineVisuals.forEach(v => {
      this.scene.remove(v);
      if (v.geometry) v.geometry.dispose();
      if (v.material) v.material.dispose();
    });
    this.bendLineVisuals = [];

    // Remove all features
    this.features.forEach(feature => {
      if (feature.mesh) {
        this.scene.remove(feature.mesh);
        if (feature.mesh.geometry) feature.mesh.geometry.dispose();
        if (feature.mesh.material) feature.mesh.material.dispose();
      }
    });

    this.features.clear();
    this.baseFlanges.clear();
    this.unfoldedParts.clear();

    console.log('[SheetMetal] Workbench disposed');
  }
}

// Make available globally
if (typeof window !== 'undefined') {
  window.SheetMetalWorkbench = SheetMetalWorkbench;
}
